import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { PostHogProvider } from "@posthog/react";
import posthog from "posthog-js";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Dashboard from "./pages/Dashboard";
import CloudDataTableView from "./pages/DataTable/CloudDataTableView";
import MagicLinkCallback from "./pages/Auth/MagicLinkCallback";
import SignIn from "./pages/Auth/SignIn";
import InvitationLanding from "./pages/Auth/InvitationLanding";
import CheckoutWizard from "./pages/Checkout/CheckoutWizard";
import ConnectClaude from "./pages/ConnectClaude/ConnectClaude";
import OAuthConsent from "./pages/OAuth/OAuthConsent";
import UninstallFeedback from "./pages/Uninstall/UninstallFeedback";
import ConnectionError from "./components/ConnectionError";

function AppContent() {
  const { isApiConnected, retryConnection, loading } = useAuth();

  // The uninstall page only records an event and redirects to a feedback form —
  // render it immediately, bypassing the auth loading gate (and its API check)
  // so there's no blank wait before the redirect.
  if (window.location.pathname === "/uninstall") {
    return <UninstallFeedback />;
  }

  // Don't show connection error during initial loading
  if (loading) {
    return null;
  }

  return (
    <>
      <Router>
        <Routes>
          {/* Sign in route */}
          <Route path="/signin" element={<SignIn />} />

          {/* Dashboard routes */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/:view" element={<Dashboard />} />

          {/* Cloud Data Table routes */}
          <Route path="/table/:tableId?" element={<CloudDataTableView />} />
          <Route path="/table" element={<CloudDataTableView />} />

          {/* Magic link callback — LEGACY, unreachable: login emails carry a code,
              not a link. Not being re-enabled; see MagicLinkCallback.jsx. */}
          <Route path="/magic-link" element={<MagicLinkCallback />} />

          {/* OAuth consent — MCP connectors (Claude etc.) land here from /oauth/authorize */}
          <Route path="/oauth/consent" element={<OAuthConsent />} />

          {/* Invitation landing — links from invitation emails arrive here */}
          <Route path="/invitation" element={<InvitationLanding />} />

          {/* Checkout wizard - standalone page for extension users */}
          <Route path="/checkout" element={<CheckoutWizard />} />

          {/* Connect — public onboarding funnel (extension + campaigns). One page,
              three stories: /connect?mcp=claude for Claude, ?mcp=chatgpt for
              ChatGPT, /connect (or ?mcp) for any other MCP agent.
              /connect-claude is the original URL and always means Claude —
              shipped extension builds link straight to it. */}
          <Route path="/connect" element={<ConnectClaude />} />
          <Route path="/connect-claude" element={<ConnectClaude />} />

          {/* Uninstall feedback - public page opened when the extension is removed */}
          <Route path="/uninstall" element={<UninstallFeedback />} />

          {/* Plans route - top-level */}
          <Route path="/plans" element={<Dashboard />} />

          {/* Credit top-ups - top-level focal page */}
          <Route path="/topups" element={<Dashboard />} />

          {/* Legacy plans page - rollback insurance */}
          <Route path="/plans-legacy" element={<Dashboard />} />

          {/* Backward compatibility: redirect old plans URL */}
          <Route path="/dashboard/plans" element={<Navigate to="/plans" replace />} />

          {/* Backward compatibility: redirect old profile URL */}
          <Route path="/dashboard/profile" element={<Navigate to="/dashboard/account" replace />} />

          {/* Default route redirects to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>

      {/* Show connection error overlay when API is unreachable */}
      {!isApiConnected && <ConnectionError onRetry={retryConnection} />}
    </>
  );
}

function App() {
  return (
    <PostHogProvider client={posthog}>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </PostHogProvider>
  );
}

export default App;
